import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Navbar.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGithub, faLinkedin, faFacebook } from '@fortawesome/free-brands-svg-icons';
import { faBars, faTimes, faSun, faMoon } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

interface NavbarProps {
  theme: string;
  toggleTheme: () => void;
}

const Navbar = ({ theme, toggleTheme }: NavbarProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const navCenterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!navCenterRef.current) return;
    const rect = navCenterRef.current.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMenuOpen(false);
    }
  };

  return (
    <nav className={`navbar ${isScrolled ? 'navbar-compact' : ''}`}>
      <div className="navbar-content">
        {/* Single unified pill */}
        <div 
          className="navbar-pill"
          ref={navCenterRef}
          onMouseMove={handleMouseMove}
          style={{
            '--cursor-x': `${mousePosition.x}px`,
            '--cursor-y': `${mousePosition.y}px`,
          } as React.CSSProperties}
        >
          <div className="navbar-center-glow" />
          <button onClick={() => scrollToSection('home-section')} className="nav-link-btn">Home</button>
          <button onClick={() => scrollToSection('about-section')} className="nav-link-btn">About</button>
          <button onClick={() => scrollToSection('experience-section')} className="nav-link-btn">Experience</button>
          <button onClick={() => scrollToSection('skills-section')} className="nav-link-btn">Skills</button>
          <button onClick={() => scrollToSection('projects-section')} className="nav-link-btn">Projects</button>
          <button onClick={() => scrollToSection('contact-section')} className="nav-link-btn">Contact</button>
          
          {/* Theme toggle - inside pill, shown when scrolled */}
          <div className="theme-toggle-wrapper">
            <div className="nav-divider" />
            <button onClick={toggleTheme} className="theme-toggle-inner" aria-label="Toggle theme">
              <FontAwesomeIcon icon={(theme === 'dark' ? faSun : faMoon) as IconProp} />
            </button>
          </div>
        </div>

        {/* Right side - visible when not scrolled */}
        <div className="navbar-right">
          <button onClick={toggleTheme} className="theme-toggle" aria-label="Toggle theme">
            <FontAwesomeIcon icon={(theme === 'dark' ? faSun : faMoon) as IconProp} />
          </button>
          <a href="https://github.com/svenkat19" target="_blank" rel="noopener noreferrer" aria-label="GitHub Profile">
            <FontAwesomeIcon icon={faGithub as IconProp} className="nav-social-icon" />
          </a>
          <a href="https://www.linkedin.com/in/shashankvenkat18/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn Profile">
            <FontAwesomeIcon icon={faLinkedin as IconProp} className="nav-social-icon" />
          </a>
          <a href="https://www.facebook.com/shaz.venk/" target="_blank" rel="noopener noreferrer" aria-label="Facebook Profile">
            <FontAwesomeIcon icon={faFacebook as IconProp} className="nav-social-icon" />
          </a>
        </div>

        <button className="mobile-menu-toggle" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
          <FontAwesomeIcon icon={(menuOpen ? faTimes : faBars) as IconProp} size="lg" />
        </button>
        <div className={`mobile-menu ${menuOpen ? 'open' : ''}`}>
          <button onClick={() => scrollToSection('home-section')} className="mobile-nav-link">Home</button>
          <button onClick={() => scrollToSection('about-section')} className="mobile-nav-link">About</button>
          <button onClick={() => scrollToSection('experience-section')} className="mobile-nav-link">Experience</button>
          <button onClick={() => scrollToSection('skills-section')} className="mobile-nav-link">Skills</button>
          <button onClick={() => scrollToSection('projects-section')} className="mobile-nav-link">Projects</button>
          <button onClick={() => scrollToSection('contact-section')} className="mobile-nav-link">Contact</button>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
